import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chartAccentTint } from "@/components/charts/chartPalette";
import { observer } from "mobx-react-lite";
import { toast } from "sonner";
import { MessagesSquare } from "lucide-react";
import { useMessagesStore } from "@/stores/messages/hooks/useMessagesStore";
import { useStores } from "@/stores/useStores";
import { useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { PMEmptyState, PMLoadingState } from "@/domains/pm/components/shared";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import type { ProjectMessage } from "@/domains/messages/types";
import { ChatComposer } from "@/components/chat/thread/ChatComposer";
import { quoteTextOf } from "@/domains/pm/utils/commentQuote";
import { messageAuthorName } from "@/domains/messages/authorName";
import { cn } from "@/lib/utils";
import { MessageList } from "./MessageList";

/** The composer's read-only reply preview: who is being replied to, then their
 *  words (truncated by quoteTextOf, exactly as in the issue comment thread). */
function replyPreview(message: ProjectMessage, replyingToTemplate: string): string {
  const who = replyingToTemplate.replace("{name}", messageAuthorName(message));
  return [who, quoteTextOf(message.body)].join("\n");
}

interface MessageBoardProps {
  buildingId: number;
  buildingProjectId: number;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  className?: string;
}

/**
 * The chat feed + composer for one project channel. Owns the load/subscribe
 * lifecycle, scroll behaviour, and the send/edit/delete/react wiring against
 * MessagesStore. Rendered by both the compact widget and the expanded modal.
 */
export const MessageBoard = observer(function MessageBoard({
  buildingId,
  buildingProjectId,
  openViewer,
  className,
}: MessageBoardProps) {
  const { t } = useLanguage();
  const store = useMessagesStore();
  const projectMembers = useProjectMembersStore();
  const currentUser = useStores().userStore.userProfile;
  const currentUserId = currentUser?.id;

  const [replyingTo, setReplyingTo] = useState<ProjectMessage | null>(null);
  // The message a reply-quote click jumped to — pulses briefly to draw the eye.
  const [highlightId, setHighlightId] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const highlightTimer = useRef<ReturnType<typeof setTimeout>>();

  // Scroll a message into view and pulse it. Toggling the highlight off then
  // on (next frame) restarts the CSS animation even on a repeat click.
  const scrollToMessage = useCallback((id: number) => {
    const el = scrollRef.current?.querySelector<HTMLElement>(`[data-message-id="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(null);
    requestAnimationFrame(() => setHighlightId(id));
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 900);
  }, []);

  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  // Mention highlight/resolution must use the SAME audience as the picker:
  // the project-scoped member list (org owners/admins + assigned subs), not the
  // org-wide user list, which isn't loaded on the dashboard — so editing a
  // message there would otherwise drop every mention row and fail to highlight.
  useEffect(() => {
    if (buildingProjectId) projectMembers.fetch(buildingProjectId);
  }, [buildingProjectId, projectMembers]);

  const projectUsers = projectMembers.get(buildingProjectId);

  const mentionDisplayNames = useMemo(
    () =>
      projectUsers
        .map((u) => `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email)
        .filter((n): n is string => Boolean(n)),
    [projectUsers]
  );

  const deriveMentionIds = (text: string): number[] => {
    const ids: number[] = [];
    for (const u of projectUsers) {
      const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email;
      if (name && text.includes(`@${name}`)) ids.push(u.id);
    }
    return ids;
  };

  // Load + subscribe whenever the active channel changes. Tears down BOTH
  // realtime channels on unmount/switch to avoid leaking websockets.
  useEffect(() => {
    if (!buildingProjectId) return;
    nearBottomRef.current = true;
    setReplyingTo(null);
    const reload = () => {
      void store.loadMessages(buildingProjectId, currentUserId ?? 0);
    };
    reload();
    store.subscribe(buildingProjectId, reload);
    if (currentUserId) {
      void store.markChannelMentionsReadSafe(buildingProjectId, currentUserId);
    }
    return () => {
      store.unsubscribe();
    };
  }, [buildingProjectId, currentUserId, store]);

  // Keep the feed pinned to the bottom when the user is already there.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (nearBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [store.messages.length, buildingProjectId]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  /** Resolves false when nothing was posted; the composer then restores the draft. */
  const handleSend = (
    body: string,
    mentionedUserIds: number[],
    documentIds: number[]
  ): Promise<boolean> => {
    if (!currentUserId) return Promise.resolve(false);
    // The reply target is the board's own state — the composer only owns the
    // read-only quote it renders above the input.
    const replyTarget = replyingTo;
    setReplyingTo(null);
    return store
      .sendMessage(
        buildingProjectId,
        currentUserId,
        body,
        mentionedUserIds,
        documentIds,
        replyTarget?.id ?? null
      )
      .then(
        () => true,
        (err: unknown) => {
          toast.error(err instanceof Error ? err.message : t.messages.sendFailed);
          // The draft comes back, so the reply it was answering comes back too.
          setReplyingTo((current) => current ?? replyTarget);
          return false;
        }
      );
  };

  const handleReact = (messageId: number, emoji: string) => {
    if (!currentUserId) return;
    store
      .toggleReaction(messageId, currentUserId, emoji)
      .catch((err: unknown) => toast.error(err instanceof Error ? err.message : "Failed to react"));
  };

  const handleEdit = (messageId: number, body: string, mentionedUserIds: number[]) => {
    if (!currentUserId) return;
    store
      .editMessage(messageId, buildingProjectId, body, currentUserId, mentionedUserIds)
      .catch((err: unknown) =>
        toast.error(err instanceof Error ? err.message : "Failed to edit message")
      );
  };

  const handleDelete = (messageId: number) => {
    if (!currentUserId) return;
    store
      .deleteMessage(messageId, buildingProjectId, currentUserId)
      .catch((err: unknown) =>
        toast.error(err instanceof Error ? err.message : "Failed to delete message")
      );
  };

  const showLoading = store.isLoading && store.messages.length === 0;
  const showEmpty = !store.isLoading && store.messages.length === 0;

  const replyQuote = replyingTo ? replyPreview(replyingTo, t.messages.replyingTo) : null;

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="folder-tree-scroll min-h-0 flex-1 overflow-y-auto"
      >
        {showLoading ? (
          <div className="flex h-full items-center justify-center">
            <PMLoadingState />
          </div>
        ) : showEmpty ? (
          <PMEmptyState
            icon={MessagesSquare}
            title={t.messages.emptyChannel}
            className="h-full px-4 py-6"
            iconColor={chartAccentTint("steel", 0.7)}
          />
        ) : (
          <MessageList
            messages={store.messages}
            currentUserId={currentUserId}
            buildingId={buildingId}
            buildingProjectId={buildingProjectId}
            mentionDisplayNames={mentionDisplayNames}
            deriveMentionIds={deriveMentionIds}
            openViewer={openViewer}
            onReply={setReplyingTo}
            onReact={handleReact}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onQuoteClick={scrollToMessage}
            highlightId={highlightId}
          />
        )}
      </div>
      <ChatComposer
        buildingId={buildingId}
        projectId={buildingProjectId}
        currentUser={currentUser}
        quote={replyQuote}
        onRemoveQuote={() => setReplyingTo(null)}
        onSend={handleSend}
        storagePathSegments={["messages"]}
        className="pt-2"
        labels={{
          placeholder: t.messages.composerPlaceholder,
          attach: t.messages.attach,
          mention: t.messages.mention,
          send: t.messages.send,
          removeQuote: t.messages.cancelReply,
        }}
      />
    </div>
  );
});
